import {
    Alert,
    AlertIcon,
    FormControl,
    FormLabel,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { RiEyeCloseLine } from 'react-icons/ri';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import FormErrorMessageWithSpacing from '../../components/form-error-message';
import FormModal from '../../components/FormModal';
import FormStack from '../../components/form-stack';
import { TranslationKeys, translate } from '../../i18n';
import { changePasswordErrorKey, validateChangePasswordForm } from './change-password';
import { useChangePassword } from './hooks/useChangePassword';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

type FieldName = 'currentPassword' | 'newPassword' | 'confirmPassword';

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmPassword: '' };
/**
 * Shared by the `touched` and `visible` maps. Both are only ever replaced with
 * fresh objects, never mutated, so one frozen constant is safe for both — and
 * reusing the same reference makes the reset below a React bail-out instead of
 * a re-render when there was nothing to clear.
 */
const ALL_FALSE: Record<FieldName, boolean> = {
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
};

/**
 * Self-service password change for the signed-in org member, opened from the
 * navbar account menu. Admins already had `admin-set-user-password` for their
 * clients and a reset-email flow for themselves; this closes the gap of
 * changing your own password while signed in.
 */
export default function ChangePasswordModal({ isOpen, onClose }: Props) {
    const toast = useToast();
    const [values, setValues] = useState(EMPTY_FORM);
    const [touched, setTouched] = useState<Record<FieldName, boolean>>(ALL_FALSE);
    const [visible, setVisible] = useState<Record<FieldName, boolean>>(ALL_FALSE);
    const [submitErrorKey, setSubmitErrorKey] = useState<TranslationKeys | null>(null);

    const { mutate: changePassword, isLoading } = useChangePassword(() => {
        toast({
            title: translate('auth.change_password_success'),
            status: 'success',
            duration: 4000,
            isClosable: true,
        });
        onClose();
    });

    // Clear on BOTH edges: opening must never show what the last person typed,
    // and closing must not leave plaintext passwords sitting in component
    // state for the rest of the session (this modal stays mounted with the
    // navbar). Resetting an already-clear form is a no-op.
    useEffect(() => {
        setValues(EMPTY_FORM);
        setTouched(ALL_FALSE);
        setVisible(ALL_FALSE);
        setSubmitErrorKey(null);
    }, [isOpen]);

    const { errors, isValid } = useMemo(
        () => validateChangePasswordForm(values),
        [values],
    );

    const setField = (field: FieldName) => (value: string) => {
        setValues((previous) => ({ ...previous, [field]: value }));
        // A server-side complaint (wrong current password, rate limit) is
        // about what was submitted, not about what is now being typed.
        setSubmitErrorKey(null);
    };

    const markTouched = (field: FieldName) => () =>
        setTouched((previous) => ({ ...previous, [field]: true }));

    const toggleVisible = (field: FieldName) => () =>
        setVisible((previous) => ({ ...previous, [field]: !previous[field] }));

    const handleSubmit = () => {
        setTouched({ currentPassword: true, newPassword: true, confirmPassword: true });
        if (!isValid || isLoading) return;
        changePassword(
            { currentPassword: values.currentPassword, newPassword: values.newPassword },
            { onError: (error) => setSubmitErrorKey(changePasswordErrorKey(error)) },
        );
    };

    const renderField = (
        field: FieldName,
        label: TranslationKeys,
        errorKey: TranslationKeys | undefined,
    ) => {
        const showError = Boolean(errorKey) && touched[field];
        return (
            <FormControl isInvalid={showError}>
                <FormLabel fontSize="sm" fontWeight="500" mb="8px">
                    {translate(label)}
                </FormLabel>
                <InputGroup size="md">
                    <Input
                        aria-label={translate(label)}
                        value={values[field]}
                        onChange={(event) => setField(field)(event.target.value)}
                        onBlur={markTouched(field)}
                        // The fields are not wrapped in a <form> (Chakra's
                        // Modal owns the markup), so Enter would otherwise do
                        // nothing on a three-field password form.
                        onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            handleSubmit();
                        }}
                        type={visible[field] ? 'text' : 'password'}
                        placeholder="********"
                        fontSize="sm"
                        autoComplete={
                            field === 'currentPassword' ? 'current-password' : 'new-password'
                        }
                    />
                    <InputRightElement display="flex" alignItems="center">
                        {/*
                          * A real button, not a clickable <svg>: an icon with
                          * role="button" is not focusable and cannot be
                          * activated from the keyboard, which is the one input
                          * method most likely to need the reveal. The label is
                          * per-field — three identically named buttons tell a
                          * screen-reader user nothing about which one they are on.
                          */}
                        <IconButton
                            type="button"
                            size="sm"
                            variant="ghost"
                            aria-label={translate('auth.change_password_toggle_visibility', {
                                field: translate(label),
                            })}
                            icon={
                                <Icon
                                    as={visible[field] ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                />
                            }
                            onClick={toggleVisible(field)}
                        />
                    </InputRightElement>
                </InputGroup>
                {showError && errorKey && (
                    <FormErrorMessageWithSpacing title={translate(errorKey)} />
                )}
            </FormControl>
        );
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={onClose}
            title={translate('auth.change_password')}
            submitLabel={translate('auth.change_password_cta')}
            cancelLabel={translate('cancel')}
            onSubmit={handleSubmit}
            isSubmitDisabled={!isValid}
            isSubmitting={isLoading}
            size="md"
        >
            <FormStack>
                <Text fontSize="sm" color="secondaryGray.600">
                    {translate('auth.change_password_description')}
                </Text>

                {submitErrorKey && (
                    <Alert status="error" borderRadius="12px" fontSize="sm">
                        <AlertIcon />
                        {translate(submitErrorKey)}
                    </Alert>
                )}

                {renderField('currentPassword', 'auth.change_password_current', undefined)}
                {renderField('newPassword', 'auth.change_password_new', errors.newPassword)}
                {renderField(
                    'confirmPassword',
                    'auth.change_password_confirm',
                    errors.confirmPassword,
                )}
            </FormStack>
        </FormModal>
    );
}
